import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { Calendar as CalendarIcon, Send, CheckCircle2, FileText, ExternalLink, RefreshCw, Upload, Paperclip } from 'lucide-react';
import { guestBooking } from '../utils/api';

/**
 * GuestBooking — public no-account booking page (roadmap item #20).
 *
 * Hits /api/guest-booking/config on mount to learn whether the
 * feature is enabled and which fields are required, then renders a
 * minimal form. On submit, posts to /api/guest-booking and shows a
 * confirmation. No auth, no token — fully public.
 */

const FIELD_LABELS = {
  email:     'Email address',
  phone:     'Phone number',
  address:   'Address',
  pet_name:  'Pet name',
  pet_breed: 'Pet breed',
  pet_size:  'Pet size',
  notes:     'Notes / special requests',
};

const SIZE_OPTIONS = [
  { value: '',         label: '— Select size —' },
  { value: 'small',    label: 'Small' },
  { value: 'medium',   label: 'Medium' },
  { value: 'large',    label: 'Large' },
  { value: 'xlarge',   label: 'Extra-large' },
];

const GuestBooking = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  // Phase 2c: track per-required-form state. Keyed by form_id:
  //   { submission_id, share_token, completed, spawning }
  // The guest clicks "Open form" (or we open automatically on first
  // mount), fills it on /forms/fill/:token, then comes back and ticks
  // "I've completed it". Server re-validates submitted_at server-side
  // so the checkbox is purely UX.
  const [formStates, setFormStates] = useState({});
  // Phase 2c (docs): per-label upload state. Keyed by document label:
  //   { document_id, filename, uploading }
  const [docStates, setDocStates] = useState({});

  // Form state. Each field maps to the same backend key.
  const [form, setForm] = useState({
    first_name: '', last_name: '',
    email: '', phone: '', address: '',
    pet_name: '', pet_breed: '', pet_size: '',
    notes: '',
    service_id: '',
    appointment_date: '',
    start_time: '',
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const res = await guestBooking.config();
      setConfig(res.data);
    } catch (e) {
      console.error('Load guest config:', e);
      toast.error('Could not load booking page');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  // Mint (or re-mint) a public share token for one required form.
  // Stores the result in formStates so the UI can render an "Open
  // form" link, and so handleSubmit can include the submission_id.
  const spawnFormToken = async (formId) => {
    setFormStates((prev) => ({
      ...prev,
      [formId]: { ...(prev[formId] || {}), spawning: true },
    }));
    try {
      const res = await guestBooking.spawnFormSubmission(formId);
      setFormStates((prev) => ({
        ...prev,
        [formId]: {
          submission_id: res.data.submission_id,
          share_token: res.data.share_token,
          completed: false,
          spawning: false,
        },
      }));
      // Open in a new tab so the booking form on this tab keeps its
      // half-filled state.
      const url = `${window.location.origin}/forms/fill/${res.data.share_token}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setFormStates((prev) => ({
        ...prev,
        [formId]: { ...(prev[formId] || {}), spawning: false },
      }));
      toast.error(e.response?.data?.error || 'Could not open form');
    }
  };

  const markFormCompleted = (formId, completed) => {
    setFormStates((prev) => ({
      ...prev,
      [formId]: { ...(prev[formId] || {}), completed },
    }));
  };

  // Upload one required document. Posts the file with its label;
  // server validates label is configured and returns a document_id we
  // post back with the booking. Re-uploading replaces (a stranded
  // older row stays in guest_documents but is unbound).
  const uploadDocument = async (label, file) => {
    if (!file) return;
    setDocStates((prev) => ({
      ...prev,
      [label]: { ...(prev[label] || {}), uploading: true },
    }));
    try {
      const res = await guestBooking.uploadDocument(label, file);
      setDocStates((prev) => ({
        ...prev,
        [label]: {
          document_id: res.data.document_id,
          filename: res.data.filename,
          uploading: false,
        },
      }));
    } catch (e) {
      setDocStates((prev) => ({
        ...prev,
        [label]: { ...(prev[label] || {}), uploading: false },
      }));
      toast.error(e.response?.data?.error || 'Upload failed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim()) {
      return toast.error('First and last name are required');
    }
    if (!form.appointment_date || !form.start_time) {
      return toast.error('Pick a date and time');
    }
    // Client-side mirror of the server's required-fields gate. Server
    // re-validates so this is purely UX feedback.
    for (const key of (config?.required_fields || [])) {
      if (!String(form[key] || '').trim()) {
        return toast.error(`${FIELD_LABELS[key] || key} is required`);
      }
    }

    // Phase 2c: required-forms gate. UX checks the "I've completed it"
    // boxes; server re-validates that each submission_id is actually
    // submitted_at IS NOT NULL.
    const submittedSubmissionIds = [];
    for (const f of (config?.required_forms || [])) {
      const state = formStates[f.id];
      if (!state?.submission_id) {
        return toast.error(`Please open and complete "${f.name}" first`);
      }
      if (!state.completed) {
        return toast.error(`Mark "${f.name}" as completed once you've submitted it`);
      }
      submittedSubmissionIds.push(state.submission_id);
    }

    // Phase 2c (docs): each required document label must have an
    // uploaded document_id. Server re-validates the binding state.
    const uploadedDocumentIds = [];
    for (const label of (config?.required_documents || [])) {
      const state = docStates[label];
      if (!state?.document_id) {
        return toast.error(`Please upload "${label}" before submitting`);
      }
      uploadedDocumentIds.push(state.document_id);
    }

    setSubmitting(true);
    try {
      const res = await guestBooking.submit({
        ...form,
        submitted_submission_ids: submittedSubmissionIds,
        uploaded_document_ids: uploadedDocumentIds,
      });
      setSuccess(res.data?.appointment || true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Booking failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12 flex justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!config?.enabled) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-xl">
        <div className="card text-center">
          <h1 className="text-2xl font-bold mb-2">Guest booking unavailable</h1>
          <p className="text-gray-600 mb-4">
            Guest booking isn't currently enabled. Please <Link to="/register" className="text-primary-600 hover:underline">create an account</Link> or <Link to="/login" className="text-primary-600 hover:underline">log in</Link> to book.
          </p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-xl">
        <div className="card text-center">
          <CheckCircle2 className="text-green-600 mx-auto mb-3" size={48} />
          <h1 className="text-2xl font-bold mb-2">Booking received</h1>
          <p className="text-gray-600 mb-4">
            Thanks {form.first_name}! We've recorded your appointment request.
            {form.email && ` We'll reach out at ${form.email} to confirm.`}
          </p>
          <Link to="/" className="btn btn-primary">Back to home</Link>
        </div>
      </div>
    );
  }

  const isRequired = (key) => (config.required_fields || []).includes(key);
  const showField = (key) => true; // Phase 1: render every field; required-only is a Phase 2 polish.

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <div className="card">
        <div className="flex items-center gap-3 mb-4">
          <CalendarIcon className="text-primary-600" size={28} />
          <h1 className="text-3xl font-bold">Book as a guest</h1>
        </div>
        <p className="text-gray-600 mb-6 text-sm">
          No account needed. Required fields are marked <span className="text-red-500">*</span>.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First name <span className="text-red-500">*</span></label>
              <input className="input" required value={form.first_name} onChange={(e) => updateField('first_name', e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last name <span className="text-red-500">*</span></label>
              <input className="input" required value={form.last_name} onChange={(e) => updateField('last_name', e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {showField('email') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email {isRequired('email') && <span className="text-red-500">*</span>}</label>
                <input type="email" className="input" required={isRequired('email')} value={form.email} onChange={(e) => updateField('email', e.target.value)} />
              </div>
            )}
            {showField('phone') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone {isRequired('phone') && <span className="text-red-500">*</span>}</label>
                <input type="tel" className="input" required={isRequired('phone')} value={form.phone} onChange={(e) => updateField('phone', e.target.value)} />
              </div>
            )}
          </div>

          {showField('address') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address {isRequired('address') && <span className="text-red-500">*</span>}</label>
              <input className="input" required={isRequired('address')} value={form.address} onChange={(e) => updateField('address', e.target.value)} />
            </div>
          )}

          {config.pet_required && (
            <div className="border-t pt-4">
              <h3 className="font-semibold text-gray-800 mb-2">Pet info</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pet name <span className="text-red-500">*</span></label>
                  <input className="input" required value={form.pet_name} onChange={(e) => updateField('pet_name', e.target.value)} />
                </div>
                {showField('pet_breed') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Breed {isRequired('pet_breed') && <span className="text-red-500">*</span>}</label>
                    <input className="input" required={isRequired('pet_breed')} value={form.pet_breed} onChange={(e) => updateField('pet_breed', e.target.value)} />
                  </div>
                )}
                {showField('pet_size') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Size {isRequired('pet_size') && <span className="text-red-500">*</span>}</label>
                    <select className="input" required={isRequired('pet_size')} value={form.pet_size} onChange={(e) => updateField('pet_size', e.target.value)}>
                      {SIZE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="border-t pt-4">
            <h3 className="font-semibold text-gray-800 mb-2">Appointment</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Service</label>
                <select className="input" value={form.service_id} onChange={(e) => updateField('service_id', e.target.value)}>
                  <option value="">— Choose a service —</option>
                  {(config.services || []).map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date <span className="text-red-500">*</span></label>
                <input type="date" className="input" required value={form.appointment_date} onChange={(e) => updateField('appointment_date', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start time <span className="text-red-500">*</span></label>
                <input type="time" className="input" required value={form.start_time} onChange={(e) => updateField('start_time', e.target.value)} />
              </div>
            </div>
          </div>

          {showField('notes') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes / special requests {isRequired('notes') && <span className="text-red-500">*</span>}</label>
              <textarea rows={3} className="input" required={isRequired('notes')} value={form.notes} onChange={(e) => updateField('notes', e.target.value)} />
            </div>
          )}

          {/* Phase 2c: required intake forms. Each "Open form" button
              spawns a fresh share-token submission, opens it in a new
              tab, and (after the guest submits the form there) the
              guest ticks "I've completed it" here. handleSubmit posts
              the submission_ids alongside the booking; server
              re-validates submitted_at IS NOT NULL. */}
          {(config.required_forms || []).length > 0 && (
            <div className="border-t pt-4">
              <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
                <FileText size={18} />
                Required intake forms
              </h3>
              <p className="text-xs text-gray-500 mb-3">
                Please complete each form below before submitting your booking.
                Each form opens in a new tab.
              </p>
              <div className="space-y-2">
                {config.required_forms.map((f) => {
                  const state = formStates[f.id] || {};
                  const opened = !!state.submission_id;
                  return (
                    <div key={f.id} className="border rounded p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium text-gray-800">{f.name}</div>
                        {f.description && <div className="text-xs text-gray-500">{f.description}</div>}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => spawnFormToken(f.id)}
                          disabled={state.spawning}
                          className="btn btn-secondary flex items-center gap-2 text-sm"
                        >
                          {opened ? <RefreshCw size={14} /> : <ExternalLink size={14} />}
                          {state.spawning ? 'Opening…' : opened ? 'Reopen form' : 'Open form'}
                        </button>
                        <label className={`flex items-center gap-2 text-sm ${opened ? '' : 'opacity-50'}`}>
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            disabled={!opened}
                            checked={!!state.completed}
                            onChange={(e) => markFormCompleted(f.id, e.target.checked)}
                          />
                          <span>I've completed it</span>
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Phase 2c (docs): required document uploads. One file per
              configured label. Re-uploading just replaces the local
              tracked id; the server-side row from a previous attempt
              stays orphaned but unbound. */}
          {(config.required_documents || []).length > 0 && (
            <div className="border-t pt-4">
              <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
                <Upload size={18} />
                Required documents
              </h3>
              <p className="text-xs text-gray-500 mb-3">
                Please upload one file per item. Images, PDFs, and Word
                documents are accepted (max 10&nbsp;MB each).
              </p>
              <div className="space-y-2">
                {config.required_documents.map((label) => {
                  const state = docStates[label] || {};
                  const uploaded = !!state.document_id;
                  return (
                    <div key={label} className="border rounded p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium text-gray-800">{label}</div>
                        {uploaded && (
                          <div className="text-xs text-green-700 flex items-center gap-1 mt-1">
                            <Paperclip size={12} /> {state.filename}
                          </div>
                        )}
                      </div>
                      <label className="btn btn-secondary flex items-center gap-2 text-sm cursor-pointer">
                        <Upload size={14} />
                        <span>{state.uploading ? 'Uploading…' : uploaded ? 'Replace file' : 'Upload file'}</span>
                        <input
                          type="file"
                          className="hidden"
                          disabled={state.uploading}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            // Reset the input so picking the same file
                            // again triggers onChange.
                            e.target.value = '';
                            uploadDocument(label, f);
                          }}
                        />
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex justify-between items-center pt-2">
            <Link to="/login" className="text-sm text-primary-600 hover:underline">Have an account? Log in</Link>
            <button type="submit" disabled={submitting} className="btn btn-primary flex items-center gap-2">
              <Send size={16} />
              {submitting ? 'Submitting…' : 'Submit booking'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GuestBooking;
